import React, { Component, useState, useRef, useEffect } from 'react'
import {Plus, PlusCircle} from 'lucide-react'
import classnames from 'classnames'
import Table from './AgGrid/Table'
import {TrashCell, AutoSizer, BooleanCell, FileCell, StatusTag, HighlightCell, FailureIndicator} from './DataTable'
import { toastr } from 'react-redux-toastr'
import getFormValidations from '../apps/KpModule/components/Form/formValidations'
import _ from 'lodash'
import moment from 'moment'
import { ObjectId } from 'bson'
import { FormField, FormButtons } from './Form'
import ModuleFormField from '../apps/KpModule/components/Form/Field'
import BsButton from './BsButton'
import { translateName } from '../utils/i18n'
import './AccordionComponent.css'
import PropTypes from 'prop-types'
import Accordion from './Accordion'
import {getFormattedAmount} from "../apps/KpModule/components/List/KpDataTable";
import ColumnSumHeader from "./AgGrid/ColumnSumHeader";
import ColumnSelector from "./DataTable/ColumnSelector";
import DataTableFilter from "./DataTable/DataTableFilter";
import DownloadDataTable from "./DataTable/DownloadDataTable";
import GlyphiconButton from "../apps/KpModule/components/ModuleAccordion/GlyphiconButton";
import ObjectEditor from "./AgGrid/editors/ObjectEditor";
import ObjectArrayEditor from "./AgGrid/editors/ObjectArrayEditor";
import PhoneNumberEditor from "./AgGrid/editors/PhoneNumberEditor";


export function generateObjectDataGetter(column) {
    return object => column.cellDataGetter({rowData: object, dataKey: column.dataKey})
}

function valueMatchesTarget(value, target) {
    const matchAsString = _.isString(value) && value.toLowerCase().search(target.toLowerCase()) !== -1
    const matchAsNumber = _.isNumber(value) && value === Number(target)
    return matchAsString || matchAsNumber
}

const getCellEditor = (column) => {
    switch (column.type) {
        case "textarea":
            return "agLargeTextCellEditor"
        case "object":
            return ObjectEditor
        case "objectArray":
            return ObjectArrayEditor
        case "phoneNumber":
            return PhoneNumberEditor
        default:
            return undefined
    }
}

export function applyFilter({ objects, filter, columns }) {
    // Save some computation time if :
    // no filter or
    // no columns or
    // no objects

    if (!columns.length || !objects) return []
    if (filter === '') return objects

    const dataGetters = columns.map(generateObjectDataGetter)

    return objects.filter(object =>
        dataGetters.some(dataGetter =>
            valueMatchesTarget(dataGetter(object), filter)
        )
    )
}

export function applySort({ objects, sortBy, sortDirection, columns }) {
    if (!sortBy) return objects

    const columnToSort = _.find(columns, { dataKey: sortBy })
    if (!columnToSort) return objects

    const dataGetter = generateObjectDataGetter(columnToSort)

    return _.orderBy(
        objects,
        object => dataGetter({rowData: object}),
        sortDirection === 'ASC' ? 'asc' : 'desc'
    )
}

const findRowIndex = (sortedObjects, rowData) => sortedObjects.findIndex(object => {
    return object.id === rowData.id
})

const translatorGetter = (field, t, language) => (data, path) => {
    const object = _.get(data, path)
    return field.translateName
        ? translateName(object, language)
        : field.translate
            ? t(object, { capitalize: true })
            : object
}

function cellDataGetter({ field, t, language }) {
    const getTranslation = rawValue => {
        if(field.translateName) return translateName(rawValue, language)
        if(field.translate) return  t(rawValue, { capitalize: true })
        return rawValue
    }
    switch (field.type) {
        case 'status':
            return ({rowData}) => {
                const object = _.get(rowData, field.path)
                if(!object) return {value: ""}
                const value = object[field.display]
                return {
                    style: object.style,
                    bStyle: value ? object.bStyle : 'none',
                    value: getTranslation(value)
                }
            }
        case 'date':
        case 'dateString':
            return ({ rowData, dataKey }) => {
                const value = _.get(rowData, dataKey)
                if (!value) return value
                const m = moment(value)
                return m.isValid() ? m.format(field.dateFormat || 'YYYY-MM-DD') : value
            }
        default:
            return ({ rowData, dataKey }) => _.get(rowData, dataKey)
    }
}

const getCellSearchText = (column, row, language, t) => {
    const translate = raw => {
        if (raw == null) return ''
        if (column.translateName) return translateName(raw, language) || ''
        if (column.translate) return t(raw, { capitalize: true })
        return String(raw)
    }
    const value = column.dataGetter
        ? column.dataGetter(row)
        : _.get(row, column.path)
    switch (column.type) {
        case 'text':
        case 'textarea':
        case 'date':
        case 'dateString':
            return _.isArray(value) ? value.map(translate).join(', ') : translate(value)
        case 'object':
        case 'dropdownObject': {
            if (!value) return ''
            return translate(column.display ? value[column.display] : value)
        }
        case 'objectArray':
        case 'tags': {
            if (!_.isArray(value)) return ''
            return value.map(o => translate(_.get(o, column.display))).join(', ')
        }
        case 'number':
        case 'editNumber':
            return getFormattedAmount(value, column.numberOfDecimals, language)
        default:
            return ''
    }
}

const countOccurrencesInString = (str, lowerSearch) => {
    if (!str) return 0
    const lowerStr = str.toLowerCase()
    let count = 0
    let cursor = 0
    let idx = lowerStr.indexOf(lowerSearch, cursor)
    while (idx !== -1) {
        count += 1
        cursor = idx + lowerSearch.length
        idx = lowerStr.indexOf(lowerSearch, cursor)
    }
    return count
}

const countMatchedOccurrences = (objects, columns, searchText, language, t) => {
    if (!searchText || !_.isArray(objects)) return 0
    const lower = String(searchText).toLowerCase()
    const visibleColumns = columns.filter(c => !c.hidden)
    return objects.reduce((count, row) => {
        const rowCount = visibleColumns.reduce((acc, col) => {
            const str = getCellSearchText(col, row, language, t)
            return acc + countOccurrencesInString(str, lower)
        }, 0)
        return count + rowCount
    }, 0)
}

const parseNumberInput = (text) => {
    const cleaned = String(text).replace(/[\s  ]/g, '').replace(',', '.')
    if (cleaned === '') return null
    const parsed = Number(cleaned)
    return Number.isNaN(parsed) ? undefined : parsed
}

// ag-grid ignores key events coming from an input inside a cell, so arrow navigation is done by hand
const focusSiblingCellInput = (params, { rowOffset = 0, colOffset = 0 }) => {
    const root = params.eGridCell?.closest('.ag-root')
    if (!root) return
    const columns = params.api.getAllDisplayedColumns()
    const colIndex = columns.findIndex(col => col.getColId() === params.column.getColId())
    const targetCol = columns[colIndex + colOffset]
    const targetRow = params.node.rowIndex + rowOffset
    if (!targetCol || targetRow < 0) return
    params.api.setFocusedCell(targetRow, targetCol)
    const input = root.querySelector(`.ag-row[row-index="${targetRow}"] [col-id="${targetCol.getColId()}"] input`)
    if (input) input.focus()
}

const EditNumberCell = ({ params, numberOfDecimals, language }) => {
    const [text, setText] = useState(null) // null => not focused, show formatted value
    const value = params.value

    const commit = () => {
        const parsed = parseNumberInput(text)
        setText(null)
        if (parsed === undefined) return // invalid input, keep the previous value
        if (parsed !== (value ?? null)) params.setValue(parsed)
    }

    // Native listener on purpose: ag-grid handles keydown on the cell element and would swallow the arrows
    // before React (which listens higher up) could stop them, so the caret could not move.
    const inputRef = useRef(null)
    useEffect(() => {
        const input = inputRef.current
        if (!input) return
        const onKeyDown = (e) => {
            const { key } = e
            if (!['Enter', 'Home', 'End', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(key)) return
            e.stopPropagation()
            if (key === 'Enter') return input.blur()
            const atStart = input.selectionStart === 0 && input.selectionEnd === 0
            const atEnd = input.selectionStart === input.value.length && input.selectionEnd === input.value.length
            const offsets = {
                ArrowUp: { rowOffset: -1 },
                ArrowDown: { rowOffset: 1 },
                ArrowLeft: atStart && { colOffset: -1 },
                ArrowRight: atEnd && { colOffset: 1 }
            }[key]
            if (!offsets) return // let the browser move the caret
            e.preventDefault()
            focusSiblingCellInput(params, offsets)
        }
        input.addEventListener('keydown', onKeyDown)
        return () => input.removeEventListener('keydown', onKeyDown)
    })

    return (
        <input
            ref={inputRef}
            type="text"
            inputMode="decimal"
            className="ag-input-field-input ag-text-field-input"
            value={text !== null ? text : (value == null ? '' : getFormattedAmount(value, numberOfDecimals, language))}
            onFocus={() => setText(value == null ? '' : String(value))}
            onChange={(e) => setText(e.target.value)}
            onBlur={commit}
            style={{ position: 'absolute', top: 0, bottom: 0, left: 0, right: 0, textAlign: 'end', paddingRight: '10px' }}
        />
    )
}

function cellRenderer(field, language, groupModel, accessId, t, searchText, readOnly) {
    switch (field.type) {
        case 'fileLink':
            return ({ data }) => {
                const cellData = _.get(data, field.path)
                return cellData
                    ? <FileCell file={cellData} language={language} groupModel={groupModel} accessId={accessId}/>
                    : null
            }
        case 'status':
            return ({ value }) => <StatusTag value={value.value} bStyle={value.bStyle} style={value.style} />
        case 'boolean':
            return ({ value }) => {
                return <BooleanCell value={value} />
            }
        case 'failureIndicator':
            return ({ value }) => <FailureIndicator value={value} />
        case 'number':
            return ({ value }) =>
                <div style={{textAlign: 'end', paddingRight: '10px'}}>
                    <HighlightCell
                        value={getFormattedAmount(value, field.numberOfDecimals, language)}
                        searchText={searchText}
                    />
                </div>
        case 'dropdownObject':
            return (params) => (
                <ObjectEditor
                    {...params.colDef.cellRendererParams}
                    value={params.value}
                    onValueChange={(newVal) => params.setValue(newVal)}
                    autoFocus={false}
                />
            )
        case 'tags':
            return (params) => {
                const rp = params.colDef.cellRendererParams
                const values = rp.rowFilter
                    ? rp.rowFilter(params.data, rp.allValues)
                    : rp.values
                return (
                    <ObjectArrayEditor
                        {...rp}
                        values={values}
                        value={params.value}
                        onValueChange={(newVal) => params.setValue(newVal)}
                        autoFocus={false}
                    />
                )
            }
        case 'editText':
            if (readOnly) return null // falls back to the default read-only cell
            return (params) => {
                const commitIfChanged = (value) => {
                    if (value != (params.value ?? '')) {
                        params.setValue(value)
                    }
                }
                return (
                    <input
                        type="text"
                        className="ag-input-field-input ag-text-field-input"
                        defaultValue={params.value ?? ''}
                        onBlur={(e) => commitIfChanged(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') commitIfChanged(e.target.value)
                        }}
                        style={{ position: 'absolute', top: 0, bottom: 0, left: 0, right: 0}}
                    />
                )
            }
        case 'editNumber':
            if (readOnly) {
                return ({ value }) =>
                    <div style={{textAlign: 'end', paddingRight: '10px'}}>
                        {getFormattedAmount(value, field.numberOfDecimals, language)}
                    </div>
            }
            return (params) => <EditNumberCell params={params} numberOfDecimals={field.numberOfDecimals} language={language}/>
        case 'text':
        case 'textarea':
        case 'object':
        case 'objectArray':
        case 'date':
        case 'dateString':
            return (p) => <HighlightCell value={p.valueFormatted ?? p.value} searchText={searchText} />
        default:
            return null
    }
}

const COLUMN_WIDTHS_STORAGE_PREFIX = 'kp-acc-column-widths-'

class AccordionComponent extends Component {
    matchedCountCache = { inputs: null, result: 0 }

    getColumnWidthsStorageKey() {
        const { module, path } = this.props
        return `${COLUMN_WIDTHS_STORAGE_PREFIX}${module?.id ?? ''}-${path}`
    }

    getSavedColumnWidths() {
        try {
            const raw = localStorage.getItem(this.getColumnWidthsStorageKey())
            return raw ? JSON.parse(raw) : {}
        } catch (e) {
            return {}
        }
    }

    onColumnResized = (event) => {
        if (!event.finished || !event.columns) return
        // Only persist widths from user-initiated resizes. Automatic sources
        // (flex, sizeColumnsToFit, autosizeColumns, gridInitializing, ...) would
        // otherwise freeze columns at their computed width and kill flex behavior.
        if (event.source !== 'uiColumnResized' && event.source !== 'uiColumnDragged') return
        try {
            const saved = this.getSavedColumnWidths()
            event.columns.forEach(col => {
                const colId = col.getColId()
                const width = col.getActualWidth()
                if (colId && width) saved[colId] = width
            })
            localStorage.setItem(this.getColumnWidthsStorageKey(), JSON.stringify(saved))
        } catch (e) {
            // ignore storage errors (quota, disabled, etc.)
        }
    }

    getMatchedCount(objects, columns, filter) {
        const { language, t } = this.props
        const inputs = { objects, columns, filter, language, t }
        const prev = this.matchedCountCache.inputs
        if (prev && Object.keys(inputs).every(k => prev[k] === inputs[k])) {
            return this.matchedCountCache.result
        }
        const result = countMatchedOccurrences(objects, columns, filter, language, t)
        this.matchedCountCache = { inputs, result }
        return result
    }

    constructor(props) {
        super(props)
        this.state = {
            mode: 'innerList',
            newFormObject: null,
            index: null,
            filter: '',
            isSearchActive: false,
            columnSelectorOpened: false,
            selectorOptions: props.listFields
                .map(column => ({
                    key: column.path,
                    label: column.tKey,
                    selected: !column.initiallyNotVisible
                })),
            editedRowId: null,
            sortBy: null,
            sortDirection: null,
            displayedRowCount: null
        }

        this.filterModel = {}

        this.changeFilter = this.changeFilter.bind(this)
        this.handleOpenColumnSelector = this.handleOpenColumnSelector.bind(this)
        this.handleChangeColumnSelection = this.handleChangeColumnSelection.bind(this)
        this.moveRow = this.moveRow.bind(this)
    }

    handleSortChanged = ({ sortBy, sortDirection }) => {
        this.setState({ sortBy, sortDirection })
    }

    handleDisplayedRowCountChanged = (count) => {
        if (count !== this.state.displayedRowCount) {
            this.setState({ displayedRowCount: count })
        }
    }

    handleFilterChanged = (filterModel) => {
        this.filterModel = filterModel
    }

    getInitialFilterModel = () => this.filterModel

    componentDidUpdate(prevProps, prevState) {
        let newColumnDetected = false
        const selectorOptions = this.props.listFields.map(column => {
            const prevOptionData = prevState.selectorOptions.find(option => option.key === column.path)
            if(!prevOptionData) {
                newColumnDetected = true
                return {
                    key: column.path,
                    label: column.tKey,
                    selected: !column.initiallyNotVisible
                }
            }
            return {
                key: column.path,
                label: column.tKey,
                selected: prevOptionData.selected
            }
        })

        if(newColumnDetected) {
            this.setState({
                selectorOptions
            })
        }
    }

    enhanceColumns = columns => {
        const { t, language, groupModel, accessId, defaultSortBy, defaultSortDirection, editable, disabled } = this.props
        const activeSortBy = this.state.sortBy !== null ? this.state.sortBy : defaultSortBy
        const activeSortDirection = this.state.sortBy !== null ? this.state.sortDirection : defaultSortDirection
        const filteredColumns = columns.filter(column => !column.hidden)
        const savedWidths = this.getSavedColumnWidths()
        return filteredColumns.map(column => {

            const rendererParams = column.type === 'dropdownObject' ? {
                cellRendererParams: {
                    values: this.getDataList(column.dataList),
                    displayKey: column.display,
                    width: column.width,
                    t
                }
            } : column.type === 'tags' ? {
                    cellRendererParams: column.rowFilter ? {
                        allValues: this.getDataList(column.dataList),
                        rowFilter: column.rowFilter,
                        displayKey: column.display,
                        width: column.width,
                        t
                    } : {
                        values: this.getDataList(column.dataList),
                        displayKey: column.display,
                        width: column.width,
                        t
                    }
            } : {}

            const editionProps = column.editable ? {
                cellEditor: getCellEditor(column),
                cellEditorParams: this.getCellEditorParams(column),
                cellEditorPopup: ["tags", "objectArray", "object", "textarea", "phoneNumber"].includes(column.type) || undefined
            } : {}
            const renderer = cellRenderer(column, language, groupModel, accessId, t, this.state.filter, !editable || disabled)
            const savedWidth = savedWidths[column.path]
            const alwaysEditable = ['dropdownObject', 'tags', 'editText', 'editNumber'].includes(column.type)
            return {
                ...column,
                colId: column.path,
                dataKey: column.path,
                headerName: t(column.label || column.tKey),
                cellDataType: column.type,
                minWidth: column.width,
                ...(savedWidth ? { width: savedWidth } : { flex: column.flexGrow }),
                filter: true,
                sortable: true,
                editable: column.editable && !alwaysEditable, //alwaysEditable because no need for double click for it to be editable
                sort: activeSortBy === column.path && activeSortDirection
                    ? activeSortDirection.toLowerCase()
                    : undefined,
                valueGetter: (p) => cellDataGetter({ field: column, language, t })({rowData: p.data, dataKey: column.path}),
                ...(column.type === 'number' && {
                    tooltipValueGetter: (p) => p.value == null ? undefined : getFormattedAmount(p.value, column.numberOfDecimals, language)
                }),
                ...(column.type === 'number' && column.tooltip && {
                    headerComponent: ColumnSumHeader,
                    headerComponentParams: {
                        t,
                        getFormattedAmount,
                        language,
                        numberOfDecimals: column.numberOfDecimals,
                        dataGetter: (object) => cellDataGetter({ field: column, language, t })({rowData: object, dataKey: column.path})
                    }
                }),
                ...(['object', 'dropdownObject'].includes(column.type) && {
                    filterValueGetter: (p) => {
                        const v = _.get(p.data, column.path)
                        if (!v) return ''
                        const raw = column.display ? v[column.display] : v
                        if (column.translateName) return translateName(raw, language)
                        if (column.translate) return t(raw, { capitalize: true })
                        return raw
                    }
                }),
                ...((column.type === 'objectArray' || column.type === 'tags') && {
                    filterValueGetter: (p) => {
                        const values = _.get(p.data, column.path)
                        if (!_.isArray(values)) return ''
                        return values
                            .map(o => {
                                const raw = _.get(o, column.display)
                                if (column.translateName) return translateName(raw, language)
                                if (column.translate) return t(raw, { capitalize: true })
                                return raw
                            })
                            .filter(Boolean)
                            .join(', ')
                    }
                }),
                valueSetter: (params) => {
                    const { newValue, data } = params
                    const validations = getFormValidations(column, t, columns, data)
                    for (const validate of validations) {
                        const error = validate(newValue, data, { t })
                        if (error) {
                            toastr.error(t('validationError'), t(error))
                            return false
                        }
                    }

                    params.data[params.colDef.dataKey] = params.newValue; // update rowData
                    return true
                },
                dataGetter: (object) => cellDataGetter({ field: column, language, t })({rowData: object, dataKey: column.path}),
                cellDataGetter: cellDataGetter({ field: column, language, t }),
                valueFormatter: this.getCellFormatter(column),
                ...(renderer && { cellRenderer: renderer }),
                ...editionProps,
                ...rendererParams
            }
        })
    }

    getCellEditorParams(column) {
        const { language } = this.props
        switch (column.type) {
            case "textarea":
                return {
                    maxLength: 999,
                    rows: 5,
                    cols: 100
                }
            case "object":
            case "objectArray":
            case "tags":
                if (column.rowFilter) {
                    const allValues = this.getDataList(column.dataList)
                    return (params) => ({
                        values: column.rowFilter(params.data, allValues),
                        displayKey: column.display,
                        width: column.width,
                        t: this.props.t
                    })
                }
                return {
                    values: this.getDataList(column.dataList),
                    displayKey: column.display,
                    width: column.width,
                    t: this.props.t
                }
            case "phoneNumber":
                return {
                    width: column.width,
                    language
                }
            default:
                return undefined
        }
    }


    getCellFormatter(column){
        const {language, t} = this.props

        const getTranslation = rawValue => {
            if(column.translateName) return translateName(rawValue, language)
            if(column.translate) return  t(rawValue, { capitalize: true })
            return rawValue
        }
        switch (column.type) {
            case "object":
                return p => {
                    return p.value && column.display
                        ? getTranslation(p.value[column.display])
                        : p.value
                }
            case "objectArray":
            case "tags":
                return p => {
                    const values = p.value

                    return values && _.isArray(values) &&
                        values.map(object => {
                            const display = _.get(object, `${column.display}`)
                            return getTranslation(display)
                        }).join(', ')
                }
            case "text":
            case "textarea":
                return p => _.isArray(p.value) ? p.value.map(getTranslation).join(', ') : getTranslation(p.value)
            default:
                return undefined
        }
    }

    getDataList(id) {
        const {dataLists} = this.props
        const dataList = dataLists.byId[id]

        return dataList.list.allIds.map(id => dataList.list.byId[id])
    }

    getDeleteColumn = (sortedObjects, t) => {
        const remove = rowData => () =>
            toastr.confirm(t('deletionConfirmation'), {
                onOk: () => {
                    const originalIndex = this.props.objects.findIndex(o => o.id === rowData.id)
                    if (originalIndex !== -1) this.props.remove(originalIndex)
                }
            })

        return {
            id: 'l_deleteButton',
            dataKey: 'id',
            headerName: '',
            width: 50,
            sortable: false,
            resizable: false,
            lockPosition: 'right',
            cellRenderer: p => {
                if(!p.data.noDeleteButtonAccess) {
                    return (
                        <TrashCell onClick={remove(p.data)}/>
                    )
                }

                return null
            }
        }
    }
    changeFilter(filter) {
        this.setState({filter})
    }

    handleOpenColumnSelector = ({ opened }) => {
        this.setState({
            columnSelectorOpened: opened
        })
    }

    handleChangeColumnSelection({ key, selected }) {
        const selectorOptions = this.state.selectorOptions.map(option => {
            return option.key === key ? { ...option, selected } : option
        })

        this.setState({selectorOptions})
    }

    moveRow(sortedObjects) {

        return (movingData, overData) => {
            const fromIndex = findRowIndex(sortedObjects, movingData);
            const toIndex = findRowIndex(sortedObjects, overData);
            this.props.move(fromIndex, toIndex)
        }
    }

    renderList() {
        const {
            initialize,
            listFields,
            maxRows,
            language,
            t,
            editFields,
            module,
            noRowsMessage,
            formField,
            newable,
            removable,
            sortable,
            disabled,
            objects,
            sortableTable,
            exportTableObject,
            applyBoard,
            onFieldChange
        } = this.props
        const defaultObject = editFields.reduce((acc, field) => {
            acc[field.path] = field.default
            return acc
        }, {})

        const visibleMap = this.state.selectorOptions.reduce(
            (acc, column) => Object.assign(acc, { [column.key]: column.selected }),
            {}
        )

        const selectedColumns = listFields.filter(
            field => visibleMap[field.path]
        )

        const enhancedColumns = this.enhanceColumns(selectedColumns)

        const sortedObjects = this.state.sortBy
            ? applySort({
                objects,
                sortBy: this.state.sortBy,
                sortDirection: this.state.sortDirection,
                columns: enhancedColumns
            })
            : objects

        const tableColumns = _.compact([
            //sortableTable && this.getOrderColumn(),
            ...enhancedColumns,
            formField
                ? removable && !disabled ? this.getDeleteColumn(sortedObjects, t) : undefined
                : module.removable ? this.getDeleteColumn(sortedObjects, t) : undefined
        ])

        const matchedCount = this.getMatchedCount(sortedObjects, enhancedColumns, this.state.filter)
        const totalCount = sortedObjects?.length
        const count = this.state.isSearchActive ? matchedCount : totalCount

        return (
            <div>
                {
                    applyBoard && (
                        <div className="AccordionComponent-Board">
                            <div className="AccordionComponent-Board-left">
                                <ColumnSelector
                                    options={this.state.selectorOptions}
                                    opened={this.state.columnSelectorOpened}
                                    open={this.handleOpenColumnSelector}
                                    changeColumnSelection={this.handleChangeColumnSelection}
                                    t={t}
                                />
                                <DownloadDataTable
                                    objects={sortedObjects}
                                    columns={enhancedColumns}
                                    filename={t(module.tKey)}
                                    exportTableObject={exportTableObject}
                                    language={language}
                                    t={t}
                                />
                            </div>

                            <div className="AccordionComponent-Board-right">
                                <span className="DataTable-Board-count">
                                    {!this.state.isSearchActive && this.state.displayedRowCount !== totalCount ? `${this.state.displayedRowCount} / ` : null} {count} {this.state.isSearchActive
                                        ? t("occurrence(s)", { disableCapitalize: true })
                                        : t("row(s)", { disableCapitalize: true })}
                                </span>
                                <DataTableFilter
                                    value={this.state.filter}
                                    onChange={this.changeFilter}
                                    onFocus={() => this.setState({isSearchActive: true})}
                                    onBlur={() => {
                                        if (!this.state.filter) {
                                            this.setState({isSearchActive: false})
                                        }
                                    }}
                                    placeholder={`${t('search')}...`}
                                />
                                {!!editFields.length && this.state.mode === 'innerList' && newable && !disabled && (
                                    <GlyphiconButton
                                        backgroundColor="#1f75cb"
                                        borderColor="#2f68b4"
                                        onClick={() => {
                                            initialize(defaultObject)
                                            this.setState({
                                                newFormObject: true,
                                                mode: 'innerForm'
                                            })
                                        }}
                                    >
                                        <Plus style={{color: 'white'}} size={17} />
                                    </GlyphiconButton>
                                )}
                            </div>
                        </div>
                    )
                }

                <div>
                    <AutoSizer disableHeight>
                        {({width}) => (
                            <Table
                                columns={tableColumns}
                                objects={sortedObjects?.map(obj => ({...obj}))}
                                width={width}
                                maxRows={maxRows}
                                onRowClick={(event) => {
                                    const target = event.event?.target;
                                    // ignore clicks inside the TrashCell or FileCell
                                    if (target?.closest('.DataTable-TrashCell') || target?.closest('.DataTable-FileCell')) {
                                        return;
                                    }

                                    if (event.data.noFormAccess) return

                                    if (!editFields.length) return

                                    initialize(event.data)
                                    const originalIndex = objects.findIndex(o => o.id === event.data.id)
                                    this.setState({
                                        mode: 'innerForm',
                                        newFormObject: false,
                                        index: originalIndex !== -1 ? originalIndex : event.rowIndex
                                    })
                                }}
                                editedRowId={this.state.editedRowId}
                                setEditedRowId={(id) => {
                                    this.setState({editedRowId: id})
                                    setTimeout(() => {
                                        this.setState({editedRowId: null})
                                    }, 3000)
                                }}
                                onChange={({object}) => {
                                    const originalIndex = objects.findIndex(o => o.id === object.id)
                                    if (originalIndex !== -1) onFieldChange(originalIndex, object)
                                }}
                                enableRowDragging={sortableTable}
                                moveRow={this.moveRow(sortedObjects)}
                                onSortChanged={this.handleSortChanged}
                                onFilterChanged={this.handleFilterChanged}
                                onDisplayedRowCountChanged={this.handleDisplayedRowCountChanged}
                                onColumnResized={this.onColumnResized}
                                getInitialFilterModel={this.getInitialFilterModel}
                                t={t}
                            />
                        )}
                    </AutoSizer>
                    {!!editFields.length && this.state.mode === 'innerList' && !applyBoard && newable && !disabled && (
                        <span
                            className={classnames(
                                // 'glyphicon',
                                // 'glyphicon-plus-sign',
                                'AccordionComponent-listNewButton'
                            )}
                            onClick={() => {
                                initialize(defaultObject)
                                this.setState({
                                    newFormObject: true,
                                    mode: 'innerForm'
                                })
                            }}
                        >
                        <PlusCircle size={22} />
                    </span>
                    )}
                </div>
            </div>
        )
    }

    renderForm() {
        const {
            objects,
            editable,
            disabled,
            failSubmit,
            succeedSubmit,
            isSubmitting,
            isPristine,
            isValid,
            editFields,
            formValues,
            push,
            onFieldChange,
            destroyForm,
            t,
            user,
            language,
            editObjectValues,
            groupModel,
            form
        } = this.props
        const fields = editFields.map(o => o.path)
        return (
            <div className="AccordionComponent-form">
                {editFields.map(field => !field.hidden && (
                    <FormField key={field.path} required={field.required} type={field.type} label={field.tKey} widthClassName={field.widthClassName} t={t}>
                        <ModuleFormField
                            field={{
                                ...field,
                                editable: editable ? field.editable : editable,
                                disabled: disabled ? disabled : field.disabled
                            }}
                            editObjectValues={editObjectValues}
                            t={t}
                            language={language}
                            groupModel={groupModel}
                            objectMode={this.state.newFormObject ? 'newObject' : 'editObject'}
                            user={user}
                            parentFormName={form}
                        />
                    </FormField>
                ))}
                <FormButtons>
                    {
                        !disabled && (this.state.newFormObject || editable) && (
                            <BsButton
                                type="button"
                                bsStyle="success"
                                disabled={(!this.state.newFormObject && isPristine) || isSubmitting}
                                onClick={() => {
                                    if (!isValid) {
                                        failSubmit(fields)
                                    } else {
                                        succeedSubmit()
                                        if (this.state.newFormObject) {
                                            const mongoId = new ObjectId()

                                            push({id: mongoId.toString(), ...formValues})
                                            this.setState({editedRowId: mongoId.toString()})
                                        } else {
                                            onFieldChange(this.state.index, formValues)
                                            this.setState({editedRowId: formValues.id})
                                        }

                                        setTimeout(() => {
                                            this.setState({editedRowId: null})
                                        }, 5000)
                                        this.setState({mode: 'innerList'})
                                        destroyForm()
                                    }
                                }}
                            >
                                Ok
                            </BsButton>
                        )
                    }
                    <BsButton
                        type="button"
                        bsStyle="default"
                        onClick={() => {
                            this.setState({mode: 'innerList'})
                            destroyForm()
                        }}
                    >
                        Cancel
                    </BsButton>
                </FormButtons>
            </div>
        )
    }

    renderPanel(panelKey) {
        return panelKey === 'innerList' ? this.renderList() : this.renderForm()
    }

    render() {
        const {path, t, required} =this.props
        return (
            <div className="AccordionComponent-Container" style={{padding: '5px'}}>
                <label className="AccordionComponent-label" >{t(path)} {required && '*'}</label>
                <Accordion
                    openedPanel={this.state.mode}
                    panelKeys={['innerList', 'innerForm']}
                    renderPanel={this.renderPanel.bind(this)}
                    fillAllHeight={false}
                />
            </div>
        )
    }
}

AccordionComponent.propTypes = {
    initialize: PropTypes.func.isRequired,
    isSubmitting: PropTypes.bool.isRequired,
    isPristine: PropTypes.bool.isRequired,
    listFields: PropTypes.array.isRequired,
    editFields: PropTypes.array.isRequired,
    maxRows: PropTypes.number,
    formValues: PropTypes.object,
    push: PropTypes.func.isRequired,
    remove: PropTypes.func.isRequired,
    onFieldChange: PropTypes.func.isRequired,
    failSubmit: PropTypes.func.isRequired,
    destroyForm: PropTypes.func.isRequired,
    language: PropTypes.string.isRequired,
    t: PropTypes.func.isRequired
}

export default AccordionComponent
